import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import clsx from 'clsx';

import { useMediaQuery } from '@/hooks/useResponsive';
import { useGesture } from '@/utils/gesture';
import { isIOS, isMobileByUA } from '@/utils/ua';

import css from './index.module.less';

// 视频资源配置 - 优先使用webm格式
const videoSources = [
  {
    webm: 'https://h2.static.yximgs.com/kcdn/cdn-kcdn112115/ide-login/home-video/portal_Importantfunctions_video1.e7d9574c97807bca.webm',
    mp4: 'https://h2.static.yximgs.com/kcdn/cdn-kcdn112115/ide-login/home-video/portal_Importantfunctions_video1.2f1afaecf6eaa539.mp4',
    poster:
      'https://h2.static.yximgs.com/kcdn/cdn-kcdn112115/ide-login/home-video/portal_Importantfunctions_video1.png',
  },
  {
    webm: 'https://h2.static.yximgs.com/kcdn/cdn-kcdn112115/ide-login/home-video/portal_Importantfunctions_video2.517b5366bcbfc57e.webm',
    mp4: 'https://h2.static.yximgs.com/kcdn/cdn-kcdn112115/ide-login/home-video/portal_Importantfunctions_video2.f670543d695ec12f.mp4',
  },
  {
    webm: 'https://h2.static.yximgs.com/kcdn/cdn-kcdn112115/ide-login/home-video/portal_Importantfunctions_video3.58e3fc9bfe18debd.webm',
    mp4: 'https://h2.static.yximgs.com/kcdn/cdn-kcdn112115/ide-login/home-video/portal_Importantfunctions_video3.443362ab3d4f0f5b.mp4',
  },
];

type ITabProps = {
  label: string;
  description: string;
  style: React.CSSProperties & {
    '--tab-process'?: string;
    '--tab-opacity'?: string;
  };
};

export function DeliverySection() {
  const isMobileSize = useMediaQuery('(max-width: 480px)');

  const isMobile = isMobileByUA() || isMobileSize;
  const isFirefox = useMemo(() => {
    if (typeof navigator === 'undefined' || !navigator.userAgent) return false;
    return /firefox/i.test(navigator.userAgent);
  }, []);
  const isIOSDevice = isIOS();
  const viewRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);
  const video3Ref = useRef<HTMLVideoElement>(null);
  const [groupStyle, setGroupStyle] = useState(() => {
    return {
      transform: isMobile ? 'translateX(0%)' : 'translateX(0%)',
    } as React.CSSProperties;
  });
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true); // 控制自动播放
  const [isInView, setIsInView] = useState(false); // 控制组件是否在可视区域内
  const [videosLoaded, setVideosLoaded] = useState([false, false, false]); // 追踪视频加载状态
  const [tabs, setTabs] = useState<ITabProps[]>([
    {
      label: 'Play Jam or Duet',
      description:
        'Jam for vibe coding and Duet for spec-driven collaboration. Think ahead and act with tools step-by-step.',
      style: {
        '--tab-process': '0',
        '--tab-opacity': '0',
      } as React.CSSProperties,
    },
    {
      label: 'Keep Progress Tracked',
      description: 'Breaks complex tasks into clear, trackable steps as to-do lists.',
      style: {
        '--tab-process': '0',
        '--tab-opacity': '0',
      } as React.CSSProperties,
    },
    {
      label: 'Break Boundaries',
      description: "Unlock agent's potential with MCP, integrating with your data and system.",
      style: {
        '--tab-process': '0',
        '--tab-opacity': '0',
      } as React.CSSProperties,
    },
  ]);

  const TOTAL_SLIDES = 3;

  // 视频加载完成事件处理
  const handleVideoLoaded = useCallback((videoIndex: number) => {
    setVideosLoaded((prev) => {
      const newState = [...prev];
      newState[videoIndex] = true;
      return newState;
    });
  }, []);

  // 视频元数据加载完成事件处理
  const handleVideoLoadedMetadata = useCallback((videoIndex: number) => {
    const videoRefs = [video1Ref, video2Ref, video3Ref];
    const video = videoRefs[videoIndex].current;
    if (video) {
      // 确保视频可以进行帧精确的定位
      video.currentTime = 0;
    }
  }, []);

  const handleSelect = useCallback(
    (idx: number) => {
      const clamped = Math.max(0, Math.min(idx, TOTAL_SLIDES - 1));
      if (isMobile) {
        // 移动端使用100%宽度切换，确保不触发任何滚动
        setGroupStyle({ transform: `translateX(${-100 * clamped}%)` });
      } else {
        // PC端不使用transform，直接切换activeIndex
        // 视频显示通过opacity控制
      }
      setActiveIndex(clamped);
    },
    [isMobile],
  );

  // 移动端手势处理
  const { onTouchStart, onTouchMove, onTouchEnd } = useGesture({
    onSwipeLeft: () => {
      if (isMobile && activeIndex < TOTAL_SLIDES - 1) {
        setAutoPlay(false); // 用户手动操作时停止自动播放
        handleSelect(activeIndex + 1);

        // 3秒后恢复自动播放（只有在可视区域内才恢复）
        setTimeout(() => {
          if (isInView) {
            setAutoPlay(true);
          }
        }, 3000);
      }
    },
    onSwipeRight: () => {
      if (isMobile && activeIndex > 0) {
        setAutoPlay(false); // 用户手动操作时停止自动播放
        handleSelect(activeIndex - 1);

        // 3秒后恢复自动播放（只有在可视区域内才恢复）
        setTimeout(() => {
          if (isInView) {
            setAutoPlay(true);
          }
        }, 3000);
      }
    },
  });

  // 视频播放控制函数 - 移动端自动播放，PC端手动控制进度
  const handleVideoPlayback = useCallback(
    (newIndex: number, progressPercent: number = 0) => {
      const videoRefs = [video1Ref, video2Ref, video3Ref];

      videoRefs.forEach((ref, refIndex) => {
        if (ref.current) {
          if (refIndex === newIndex) {
            if (isMobile) {
              // 移动端自动播放
              ref.current.play().catch(() => {
                console.warn(`视频 ${refIndex + 1} 播放失败`);
              });
            } else {
              // PC端根据滚动进度控制视频进度
              ref.current.pause(); // 确保视频暂停状态

              // 优化视频状态检查条件，降低延迟
              if (
                ref.current.duration &&
                !isNaN(ref.current.duration) &&
                ref.current.readyState >= 1 // 降低readyState要求，从2改为1
              ) {
                // 根据进度百分比设置视频当前时间
                const targetTime = ref.current.duration * (progressPercent / 100);

                // 直接设置currentTime，移除requestAnimationFrame以减少延迟
                try {
                  ref.current.currentTime = targetTime;
                } catch (error) {
                  // 如果设置失败，静默处理，避免影响用户体验
                  console.warn(`视频 ${refIndex + 1} 时间设置失败:`, error);
                }
              }
            }
          } else {
            // 暂停其他视频
            ref.current.pause();
          }
        }
      });
    },
    [isMobile, videosLoaded],
  );

  function setSelect(idx: number) {
    if (isMobile) {
      // 移动端直接切换activeIndex，绝对不进行任何滚动操作
      setActiveIndex(idx);
      setAutoPlay(false); // 用户手动操作时停止自动播放

      // 3秒后恢复自动播放（只有在可视区域内才恢复）
      setTimeout(() => {
        if (isInView) {
          setAutoPlay(true);
        }
      }, 3000);
    } else {
      // PC端通过滚动切换，并在用户点击时强制播放视频
      if (!viewRef.current) return;
      const screenHeight = window.innerHeight;
      const sectionTop = viewRef.current.offsetTop;
      const targetScrollTop =
        sectionTop + screenHeight * Math.max(0, Math.min(idx, TOTAL_SLIDES - 1));

      // 确保只在非移动端执行滚动
      if (!isMobileByUA() && window.innerWidth > 480) {
        window.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
      }

      // PC端用户点击时，滚动到对应位置（视频进度由滚动控制）
      // 滚动完成后视频进度会自动更新
    }
  }

  // 监听 isMobile 变化，更新轮播样式
  useEffect(() => {
    if (isMobile) {
      // 移动端使用100%宽度切换
      setGroupStyle({ transform: `translateX(${-100 * activeIndex}%)` });
    } else {
      // PC端不使用transform，通过opacity控制视频显示
      setGroupStyle({});
    }
  }, [isMobile, activeIndex]);

  // 监听 activeIndex 变化，控制视频播放和进度条
  useEffect(() => {
    // 只有移动端才自动播放视频，PC端通过滚动控制进度
    if (isMobile) {
      handleVideoPlayback(activeIndex, 0); // 移动端播放视频

      // 更新进度条状态 - 移动端特殊处理
      setTabs((preTabs) =>
        preTabs.map((tab, i) => {
          if (i < activeIndex) {
            tab.style = { '--tab-process': '100%', '--tab-opacity': '0' } as ITabProps['style'];
          } else if (i === activeIndex) {
            tab.style = {
              '--tab-process': '100%',
              '--tab-opacity': '1',
            } as ITabProps['style'];
          } else {
            tab.style = { '--tab-process': '0%', '--tab-opacity': '0' } as ITabProps['style'];
          }
          return tab;
        }),
      );
    }
    // PC端的视频进度控制在handleScroll中处理，这里不做处理
  }, [activeIndex, handleVideoPlayback, isMobile]);

  // 添加节流控制，避免过度频繁的滚动事件处理
  const lastScrollTime = useRef(0);
  const scrollThrottleDelay = 8; // 8ms节流，约120fps

  // PC端滚动控制和移动端自动轮播的处理函数
  const handleScroll = useCallback(() => {
    // 移动端严格禁止执行任何滚动相关逻辑
    if (isMobile || isMobileByUA() || window.innerWidth <= 480) {
      return;
    }

    // 简单的节流控制
    const now = performance.now();
    if (now - lastScrollTime.current < scrollThrottleDelay) {
      return;
    }
    lastScrollTime.current = now;

    if (!viewRef.current) return;

    const screenHeight = window.innerHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const sectionTop = viewRef.current.offsetTop;
    const relativeScreens = (scrollTop - sectionTop) / screenHeight;

    // 在区域外时重置进度条，但不调用handleSelect避免意外滚动
    if (relativeScreens < 0) {
      setActiveIndex(0);
      setTabs((preTabs) =>
        preTabs.map((tab) => ({
          ...tab,
          style: { '--tab-process': '0%', '--tab-opacity': '0' } as ITabProps['style'],
        })),
      );
      return;
    }

    // 滚动超出总区域时，保持最后一张图片激活状态
    if (relativeScreens >= TOTAL_SLIDES) {
      setActiveIndex(TOTAL_SLIDES - 1);
      setTabs((preTabs) =>
        preTabs.map((tab, i) => ({
          ...tab,
          style: {
            '--tab-process': i < TOTAL_SLIDES ? '100%' : '0%',
            '--tab-opacity': i === TOTAL_SLIDES - 1 ? '1' : '0',
          } as ITabProps['style'],
        })),
      );
      return;
    }

    // 计算当前显示的卡片索引和进度
    const currentSlideIndex = Math.floor(relativeScreens);
    const progressInCurrentSlide = relativeScreens - currentSlideIndex;
    const clampedIndex = Math.max(0, Math.min(currentSlideIndex, TOTAL_SLIDES - 1));

    // 更新当前激活的幻灯片，使用setActiveIndex而不是handleSelect避免意外滚动
    setActiveIndex(clampedIndex);

    // 优化视频播放进度计算，提高精度和平滑度
    const videoProgressPercent = Math.max(0, Math.min(progressInCurrentSlide * 100, 100));

    // 添加进度平滑处理，避免跳跃
    const smoothedProgress = Math.round(videoProgressPercent * 10) / 10; // 保留1位小数

    // 更新视频播放进度（PC端根据滚动控制视频进度）
    handleVideoPlayback(clampedIndex, smoothedProgress);

    // 更新进度条状态 - 改进的逻辑，让进度条更平滑地反映滚动进度
    setTabs((preTabs) =>
      preTabs.map((tab, i) => {
        if (i < clampedIndex) {
          // 已完成的进度条：100%填充，不显示光效
          return {
            ...tab,
            style: {
              '--tab-process': '100%',
              '--tab-opacity': '0',
            } as ITabProps['style'],
          };
        } else if (i === clampedIndex) {
          // 当前激活的进度条：根据滚动进度填充，显示光效
          return {
            ...tab,
            style: {
              '--tab-process': `${smoothedProgress}%`,
              '--tab-opacity': '1',
            } as ITabProps['style'],
          };
        } else {
          // 未到达的进度条：0%填充，不显示光效
          return {
            ...tab,
            style: {
              '--tab-process': '0%',
              '--tab-opacity': '0',
            } as ITabProps['style'],
          };
        }
      }),
    );
  }, [handleVideoPlayback, isMobile]);

  // 初始化视频预加载
  useEffect(() => {
    const videoRefs = [video1Ref, video2Ref, video3Ref];

    videoRefs.forEach((ref, index) => {
      if (ref.current && !videosLoaded[index]) {
        const video = ref.current;

        // 确保视频可以进行帧精确的查找
        video.load();

        // 添加错误处理
        const handleError = () => {
          console.warn(`视频 ${index + 1} 加载失败`);
        };

        video.addEventListener('error', handleError);

        // 清理函数
        return () => {
          video.removeEventListener('error', handleError);
        };
      }
    });
  }, [videosLoaded]);

  // 视频点击控制 - PC端禁用点击操作，移动端正常操作
  const handleVideoClick = useCallback(
    (videoIndex: number) => {
      // PC端禁用点击触发播放和停止操作
      if (!isMobile) {
        return;
      }

      const videoRefs = [video1Ref, video2Ref, video3Ref];
      const currentVideo = videoRefs[videoIndex].current;

      if (currentVideo) {
        // 移动端正常的播放/暂停逻辑
        if (currentVideo.paused) {
          currentVideo.play().catch(() => {
            console.warn(`视频 ${videoIndex + 1} 播放失败`);
          });
        } else {
          currentVideo.pause();
        }
      }
    },
    [isMobile],
  );

  // 使用 Intersection Observer 监听组件是否在可视区域内
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.3, // 当组件30%可见时触发
        rootMargin: '0px 0px -100px 0px', // 提前100px触发
      },
    );

    if (viewRef.current) {
      observer.observe(viewRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    // 移动端不使用滚动控制，使用简单的自动轮播，但只有在可视区域内才启动
    if (isMobile && autoPlay && isInView) {
      const interval = setInterval(() => {
        setActiveIndex((prev) => (prev + 1) % TOTAL_SLIDES);
      }, 3000); // 3秒自动切换

      return () => clearInterval(interval);
    }

    // 桌面端滚动控制逻辑 - 添加更严格的移动端检测
    const isReallyMobile = isMobileByUA() || window.innerWidth <= 480;
    if (!isMobile && !isReallyMobile) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 再次确认不是移动端才添加滚动监听
            if (!isMobileByUA() && window.innerWidth > 480) {
              window.addEventListener('scroll', handleScroll, { passive: true });

              // 进入视口时确保所有视频都开始预加载
              const videoRefs = [video1Ref, video2Ref, video3Ref];
              videoRefs.forEach((ref) => {
                if (ref.current && ref.current.readyState < 2) {
                  ref.current.load();
                }
              });

              // 初始化时调用一次，确保状态正确
              handleScroll();
            }
          } else {
            window.removeEventListener('scroll', handleScroll);
          }
        });
      });

      if (viewRef.current) {
        observer.observe(viewRef.current);
      }

      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isMobile, autoPlay, isInView, handleScroll]);

  return (
    <div className={clsx(css['delivery-section'], isMobile && css['mobile-layout'])} ref={viewRef}>
      {/* 主要的 sticky 容器 */}
      <div className={clsx(css['main-container'], isMobile && css['mobile-container'])}>
        {/* Top Banner */}
        <div className={clsx(css['banner-section'], isMobile && css['mobile-banner'])}>
          <div className={clsx(css['banner-content'])}>
            <h1 className={clsx(css['banner-title'], isMobile && css['mobile-title'])}>
              Deliver Autonomously
            </h1>
            <div
              className={clsx(
                css['banner-badge'],
                isMobile && css['mobile-badge'],
                isFirefox && css['banner-badge--ff'],
              )}
            >
              <svg
                className={clsx(css['banner-icon'])}
                width="17"
                height="18"
                viewBox="0 0 17 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.740234"
                  y="0.959473"
                  width="15.3825"
                  height="16"
                  rx="2.28571"
                  fill="#32EDDA"
                />
                <path
                  d="M5.96973 17.0657L3.66797 14.7737L5.25488 10.0881H8.16113L5.96973 17.0657ZM13.1914 3.56079L11.7061 8.01392H14.6865L11.6943 16.9895L9.33496 14.7405L11.6943 8.01392H8.7168L10.9277 0.995361L13.1914 3.56079ZM7.38086 3.48462L5.24707 10.0881H2.20703L5.11719 1.01978L7.38086 3.48462Z"
                  fill="black"
                />
              </svg>

              <span>Plan · Track · Deliver</span>
            </div>
          </div>
          <div aria-label="bg" className={css.bg} />
        </div>

        {/* Carousel Section */}
        <div className={clsx(css['carousel-container'], isMobile && css['mobile-carousel'])}>
          <div
            ref={carouselRef}
            className={clsx(css['carousel-wrapper'], isMobile && css['mobile-wrapper'])}
            onTouchStart={isMobile ? onTouchStart : undefined}
            onTouchMove={isMobile ? onTouchMove : undefined}
            onTouchEnd={isMobile ? onTouchEnd : undefined}
          >
            <div className={clsx(css['carousel-group'])} style={groupStyle}>
              {isMobile ? (
                <>
                  {/* 移动端：保持原有轮播逻辑 */}
                  {/* Slide 1 - Play Jam or Duet */}
                  <div className={clsx(css['carousel-slide'])}>
                    <div className={clsx(css['mobile-slide-content'])}>
                      <video
                        muted
                        loop={false} // iOS移动端禁用循环播放
                        preload={isIOSDevice ? undefined : 'auto'}
                        playsInline={true} // 启用内联播放
                        controls={false} // 完全禁用控制条
                        disablePictureInPicture // 禁用画中画
                        {...(isIOSDevice && { 'webkit-playsinline': 'true' })} // iOS Safari兼容
                        {...(isMobile && { 'x5-video-player-type': 'h5' })} // 微信内置浏览器兼容
                        {...(isMobile && { 'x5-video-player-fullscreen': 'false' })} // 禁用腾讯浏览器全屏
                        {...(isMobile && { 'x5-video-orientation': 'portraint' })} // 强制竖屏
                        ref={video1Ref}
                        className={clsx(css['slide-video'])}
                        onClick={() => handleVideoClick(0)}
                        onLoadedData={() => handleVideoLoaded(0)}
                        onLoadedMetadata={() => handleVideoLoadedMetadata(0)}
                        style={{ cursor: 'default' }}
                      >
                        <source src={videoSources[0].mp4} type="video/mp4" />
                        <source src={videoSources[0].webm} type="video/webm" />
                      </video>
                    </div>
                  </div>

                  {/* Slide 2 - Keep Progress Tracked */}
                  <div className={clsx(css['carousel-slide'])}>
                    <div className={clsx(css['mobile-slide-content'])}>
                      <video
                        muted
                        loop={false} // iOS移动端禁用循环播放
                        preload={isIOSDevice ? undefined : 'auto'}
                        playsInline={true} // 启用内联播放
                        controls={false} // 完全禁用控制条
                        disablePictureInPicture // 禁用画中画
                        {...(isIOSDevice && { 'webkit-playsinline': 'true' })} // iOS Safari兼容
                        {...(isMobile && { 'x5-video-player-type': 'h5' })} // 微信内置浏览器兼容
                        {...(isMobile && { 'x5-video-player-fullscreen': 'false' })} // 禁用腾讯浏览器全屏
                        {...(isMobile && { 'x5-video-orientation': 'portraint' })} // 强制竖屏
                        ref={video2Ref}
                        className={clsx(css['slide-video'])}
                        onClick={() => handleVideoClick(1)}
                        onLoadedData={() => handleVideoLoaded(1)}
                        onLoadedMetadata={() => handleVideoLoadedMetadata(1)}
                        style={{ cursor: 'default' }}
                      >
                        <source src={videoSources[1].mp4} type="video/mp4" />
                        <source src={videoSources[1].webm} type="video/webm" />
                      </video>
                    </div>
                  </div>

                  {/* Slide 3 - Break Boundaries */}
                  <div className={clsx(css['carousel-slide'])}>
                    <div className={clsx(css['mobile-slide-content'])}>
                      <video
                        muted
                        loop={false} // iOS移动端禁用循环播放
                        preload={isIOSDevice ? undefined : 'auto'}
                        playsInline={true} // 启用内联播放
                        controls={false} // 完全禁用控制条
                        disablePictureInPicture // 禁用画中画
                        {...(isIOSDevice && { 'webkit-playsinline': 'true' })} // iOS Safari兼容
                        {...(isMobile && { 'x5-video-player-type': 'h5' })} // 微信内置浏览器兼容
                        {...(isMobile && { 'x5-video-player-fullscreen': 'false' })} // 禁用腾讯浏览器全屏
                        {...(isMobile && { 'x5-video-orientation': 'portraint' })} // 强制竖屏
                        ref={video3Ref}
                        className={clsx(css['slide-video'])}
                        onClick={() => handleVideoClick(2)}
                        onLoadedData={() => handleVideoLoaded(2)}
                        onLoadedMetadata={() => handleVideoLoadedMetadata(2)}
                        style={{ cursor: 'default' }}
                      >
                        <source src={videoSources[2].mp4} type="video/mp4" />
                        <source src={videoSources[2].webm} type="video/webm" />
                      </video>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* PC端：闪动切换，只显示当前激活的视频 */}
                  <div className={clsx(css['pc-video-container'])}>
                    <div className={clsx(css['slide-content'])}>
                      <div className={clsx(css['video-wrapper'])}>
                        {/* Video 1 */}
                        <video
                          muted
                          loop
                          preload="auto"
                          ref={video1Ref}
                          className={clsx(css['slide-video'])}
                          onClick={() => handleVideoClick(0)}
                          onLoadedData={() => handleVideoLoaded(0)}
                          onLoadedMetadata={() => handleVideoLoadedMetadata(0)}
                          style={{
                            cursor: 'default',
                            opacity: activeIndex === 0 ? 1 : 0,
                            pointerEvents: activeIndex === 0 ? 'auto' : 'none',
                            display: activeIndex === 0 ? 'block' : 'none',
                          }}
                        >
                          <source src={videoSources[0].webm} type="video/webm" />
                          <source src={videoSources[0].mp4} type="video/mp4" />
                        </video>

                        {/* Video 2 */}
                        <video
                          muted
                          loop
                          preload="auto"
                          ref={video2Ref}
                          className={clsx(css['slide-video'])}
                          onClick={() => handleVideoClick(1)}
                          onLoadedData={() => handleVideoLoaded(1)}
                          onLoadedMetadata={() => handleVideoLoadedMetadata(1)}
                          style={{
                            cursor: 'default',
                            opacity: activeIndex === 1 ? 1 : 0,
                            pointerEvents: activeIndex === 1 ? 'auto' : 'none',
                            display: activeIndex === 1 ? 'block' : 'none',
                          }}
                        >
                          <source src={videoSources[1].webm} type="video/webm" />
                          <source src={videoSources[1].mp4} type="video/mp4" />
                        </video>

                        {/* Video 3 */}
                        <video
                          muted
                          loop
                          preload="auto"
                          ref={video3Ref}
                          className={clsx(css['slide-video'])}
                          onClick={() => handleVideoClick(2)}
                          onLoadedData={() => handleVideoLoaded(2)}
                          onLoadedMetadata={() => handleVideoLoadedMetadata(2)}
                          style={{
                            cursor: 'default',
                            opacity: activeIndex === 2 ? 1 : 0,
                            pointerEvents: activeIndex === 2 ? 'auto' : 'none',
                            display: activeIndex === 2 ? 'block' : 'none',
                          }}
                        >
                          <source src={videoSources[2].webm} type="video/webm" />
                          <source src={videoSources[2].mp4} type="video/mp4" />
                        </video>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Description Section */}
        <div className={clsx(css['description-section'], isMobile && css['mobile-description'])}>
          {/* Progress Bars */}
          <div className={clsx(css['progress-bars'], isMobile && css['mobile-progress'])}>
            {tabs.map((_, idx) => (
              <div key={idx} className={clsx(css['progress-bar'])}>
                <div className={clsx(css['progress-fill'])} style={tabs[idx].style}>
                  {/* 光晕效果 - 只在进度未完成时显示 */}
                  <div
                    className={clsx(css['progress-glow'])}
                    style={{
                      opacity: tabs[idx].style['--tab-opacity'],
                      display: tabs[idx].style['--tab-process'] === '100%' ? 'none' : 'block',
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>

          {/* Description Cards */}
          <div className={clsx(css['description-cards'], isMobile && css['mobile-cards'])}>
            {tabs.map((tab, idx) => (
              <div
                key={idx}
                className={clsx(
                  css['description-card'],
                  activeIndex === idx && css['active-card'],
                  isMobile && css['mobile-card'],
                )}
                onClick={() => setSelect(idx)}
              >
                <h3 className={clsx(css['card-title'], activeIndex === idx && css['active-title'])}>
                  {tab.label}
                </h3>
                <p
                  className={clsx(
                    css['card-description'],
                    activeIndex === idx && css['active-description'],
                  )}
                >
                  {tab.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
